import { useMutation, useQueryClient } from '@tanstack/react-query';

import { customerSaleQueryOptions } from '@/api/customer/customerSaleQuery';
import type { CustomerSaleResponse } from '@/api/customer/customerSaleQuery/types';
import { useToast } from '@/components/_shared/Toaster';
import apiClient from '@/constants/http';
import { useErrorTranslations } from '@/hooks/useErrorTranslations';
import { Logger } from '@/utils/logging';

import type {
  ApplyTipMutationContext,
  ApplyTipParams,
  UseApplyTipMutationOptions,
} from './types';

const applyTip = async ({
  saleOrderId,
  tips,
}: ApplyTipParams): Promise<CustomerSaleResponse> => {
  try {
    const { data } = await apiClient.put<CustomerSaleResponse>(
      `/v1/customer-sale/${saleOrderId}/tip`,
      { tips },
    );

    return data;
  } catch (error) {
    Logger.error('Error applying tip', { error });
    throw error;
  }
};

/**
 * React Query mutation hook for applying tips to a customer sale
 *
 * @param options - Mutation configuration options
 * @returns Mutation result with updated sale data
 */
export const useApplyTipMutation = (options?: UseApplyTipMutationOptions) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useErrorTranslations('appointment');

  return useMutation({
    mutationFn: applyTip,
    onMutate: async (variables) => {
      const queryKey = customerSaleQueryOptions(variables.saleOrderId).queryKey;

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousCustomerSale =
        queryClient.getQueryData<CustomerSaleResponse>(queryKey);

      // Optimistically update to the new value
      if (previousCustomerSale) {
        queryClient.setQueryData<CustomerSaleResponse>(queryKey, (old) => {
          if (!old) return old;

          // Create a map of barberId -> tip updates for quick lookup
          const tipUpdates = new Map(
            variables.tips.map((tip) => [tip.barberId, tip]),
          );

          // Update items with new tip amounts, distributing proportionally
          // among same-barber items based on each item's service subtotal
          const updatedItems = old.items.map((item) => {
            const tipUpdate = tipUpdates.get(item.barberId);
            if (!tipUpdate) return item;

            // Calculate proportional tip for same-barber group appointments
            const sameBarberItems = old.items.filter(
              (i) => i.barberId === item.barberId,
            );

            let itemTipAmount = tipUpdate.amount;

            if (sameBarberItems.length > 1) {
              const totalSubtotal = sameBarberItems.reduce(
                (sum, i) =>
                  sum +
                  (i.appointment?.costs?.services?.totalWithoutTaxes ?? 0),
                0,
              );
              const itemSubtotal =
                item.appointment?.costs?.services?.totalWithoutTaxes ?? 0;

              itemTipAmount =
                totalSubtotal > 0
                  ? Math.round(
                      tipUpdate.amount * (itemSubtotal / totalSubtotal),
                    )
                  : Math.round(tipUpdate.amount / sameBarberItems.length);
            }

            return {
              ...item,
              tipAmount: itemTipAmount,
              tipPercentage: tipUpdate.tipPercentage ?? null,
              ...(item.appointment && {
                appointment: {
                  ...item.appointment,
                  tipAmount: itemTipAmount,
                },
              }),
            };
          });

          return {
            ...old,
            items: updatedItems,
          };
        });
      }

      // Return a context object with the snapshotted value
      return { previousCustomerSale } as ApplyTipMutationContext;
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (error, variables, context, mutationContext) => {
      Logger.error('Error applying tip', { error, variables });

      // Rollback to the previous value on error
      if (context?.previousCustomerSale) {
        const queryKey = customerSaleQueryOptions(
          variables.saleOrderId,
        ).queryKey;
        queryClient.setQueryData<CustomerSaleResponse>(
          queryKey,
          context.previousCustomerSale,
        );
      }

      const message =
        error instanceof Error || 'message' in error
          ? error.message
          : t('unknownError');

      toast({
        variant: 'error',
        message: t('tipUpdateFailed'),
        description: message,
      });

      options?.onError?.(error, variables, context, mutationContext);
    },
    // Always refetch after error or success to ensure consistency
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({
        queryKey: customerSaleQueryOptions(variables.saleOrderId).queryKey,
      });
    },
    onSuccess: (data, variables, context, mutationContext) => {
      options?.onSuccess?.(data, variables, context, mutationContext);
    },
    retry: false,
    ...options,
  });
};
